
import { useEffect, useState } from "react";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { usePasswordReset } from "@/hooks/usePasswordReset";
import { AuthStatus } from "@/components/auth/AuthStatus";
import { PasswordResetDialog } from "@/components/auth/PasswordResetDialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Mail, Key, Phone, Lock, User, ExternalLink } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const Login = () => {
  const {
    error,
    userId,
    userRole,
    isResettingPassword,
    setIsResettingPassword
  } = useAuthState();

  const {
    showResetDialog,
    setShowResetDialog,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    handlePasswordRecovery,
    handlePasswordReset,
    resetPasswordEmail,
    setResetPasswordEmail,
    handleSendPasswordResetEmail,
    isRecoveryFlow,
    isSubmitting
  } = usePasswordReset(setIsResettingPassword);

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const mobile = useIsMobile();

  useEffect(() => {
    const checkForRecovery = async () => {
      const isRecovery = await handlePasswordRecovery();
      if (isRecovery) {
        setIsResettingPassword(true);
      }
    };
    
    checkForRecovery();
    
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    if (!email || !password) {
      setLoginError("Please enter both email and password");
      return;
    }
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      setLoginError(error.message);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    if (!email || !password) {
      setLoginError("Please enter both email and password");
      return;
    }
    
    const { error } = await supabase.auth.signUp({
      email,
      password
    });
    
    if (error) {
      setLoginError(error.message);
    } else {
      setLoginError("Check your email for the confirmation link!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className={`sm:mx-auto sm:w-full sm:max-w-md transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="flex justify-center">
          <div className="h-20 w-20 rounded-full bg-blue-600 flex items-center justify-center animate-pulse-glow">
            <Lock className="h-10 w-10 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 animate-typewriter">
          Welcome to Bayzo
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in to start sharing or finding parking spots
        </p>
      </div>

      <div className={`mt-8 sm:mx-auto sm:w-full sm:max-w-md transition-all duration-700 delay-300 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border border-blue-100">
          <AuthStatus 
            error={error}
            userId={userId}
            userRole={userRole}
          />
          
          {!showPasswordReset ? (
            <>
              {!isRecoveryFlow && !showEmailLogin && (
                <>
                  {mobile ? (
                    <div className="space-y-4">
                      <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 mb-4">
                        <p className="text-sm text-yellow-800">
                          Google login is not available in the app due to security policies. Please use email login instead.
                        </p>
                      </div>
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700 transition-colors"
                        onClick={() => setShowEmailLogin(true)}
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        Continue with Email
                      </Button>
                      <div className="text-center text-sm text-gray-500 mt-2">
                        <a 
                          href="https://vwyuufoxravkdroyqfps.lovableproject.com/login" 
                          target="_blank" 
                          rel="noreferrer"
                          className="flex items-center justify-center text-blue-600 hover:text-blue-800 mt-4"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Open in browser for all login options
                        </a>
                      </div>
                    </div>
                  ) : (
                    <Auth
                      supabaseClient={supabase}
                      appearance={{ 
                        theme: ThemeSupa,
                        style: {
                          button: { background: 'rgb(37 99 235)', color: 'white', borderRadius: '0.375rem', padding: '0.625rem 1.25rem' },
                          anchor: { color: 'rgb(37 99 235)' },
                          container: { gap: '16px' },
                          divider: { margin: '24px 0' },
                          label: { color: '#4B5563', fontWeight: '500', marginBottom: '4px' },
                          input: { 
                            borderRadius: '0.375rem', 
                            padding: '0.625rem 1rem',
                            backgroundColor: '#F9FAFB',
                            border: '1px solid #D1D5DB',
                            fontSize: '0.875rem'
                          },
                          message: {
                            backgroundColor: '#FEF2F2',
                            color: '#B91C1C',
                            borderRadius: '0.25rem',
                            padding: '0.5rem',
                            fontSize: '0.875rem',
                            marginBottom: '0.5rem'
                          }
                        }
                      }}
                      providers={["google"]}
                      redirectTo={`${window.location.origin}/login`}
                      theme="light"
                    />
                  )}
                </>
              )}
              
              {showEmailLogin && (
                <div className="space-y-4 animate-fade-in">
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    {loginError && (
                      <div className="bg-red-50 p-3 rounded-md border border-red-200">
                        <p className="text-sm text-red-600">{loginError}</p>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <div className="mt-1 relative">
                        <Input 
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="pl-10"
                        />
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="password">Password</Label>
                      <div className="mt-1 relative">
                        <Input 
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className="pl-10"
                        />
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Sign in</Button>
                    <Button 
                      type="button" 
                      onClick={handleSignup} 
                      variant="outline" 
                      className="w-full mt-2"
                    >
                      Create account
                    </Button>
                    <Button 
                      type="button" 
                      onClick={() => setShowEmailLogin(false)} 
                      variant="link" 
                      className="w-full mt-2"
                    >
                      Back to options
                    </Button>
                  </form>
                </div>
              )}
              
              <div className="mt-6">
                <Button 
                  variant="outline" 
                  className="w-full flex gap-2 items-center justify-center transition-all hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300" 
                  onClick={() => setShowPasswordReset(true)}
                >
                  <Key className="h-4 w-4" />
                  Forgot Password? Reset it here
                </Button>
              </div>
              
              {!showEmailLogin && !mobile && (
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline" 
                      className="flex gap-2 items-center justify-center transition-all hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                      onClick={() => setShowEmailLogin(true)}
                    >
                      <Mail className="h-4 w-4" />
                      Email
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex gap-2 items-center justify-center transition-all hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                    >
                      <Phone className="h-4 w-4" />
                      Phone
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4 animate-fade-in">
              <h3 className="text-lg font-medium text-gray-900">Reset Your Password</h3>
              <p className="text-sm text-gray-500">
                Enter your email address below and we'll send you a password reset link.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-500" />
                  Email
                </Label>
                <div className="relative">
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="Enter your email"
                    value={resetPasswordEmail}
                    onChange={(e) => setResetPasswordEmail(e.target.value)}
                    className="pl-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>
              
              <div className="flex space-x-2">
                <Button 
                  onClick={handleSendPasswordResetEmail}
                  disabled={!resetPasswordEmail}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Send Reset Link
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPasswordReset(false)}
                  className="flex-1 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-all"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            By signing in, you agree to our{" "}
            <a href="/terms" className="text-blue-600 hover:text-blue-800 transition-colors">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 transition-colors">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>

      <PasswordResetDialog
        showResetDialog={showResetDialog}
        setShowResetDialog={setShowResetDialog}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        handlePasswordReset={handlePasswordReset}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Login;
